/**
 * GARCH(1,1) Path Generator for Volatility Clustering
 * 
 * Implements Generalized Autoregressive Conditional Heteroskedasticity modeling
 * to capture realistic volatility dynamics in financial returns.
 * 
 * GARCH(1,1) equation: σ²_t = α₀ + α₁ε²_{t-1} + β₁σ²_{t-1}
 */

export interface GARCHParameters {
  alpha0: number; // Long-term variance (ω)
  alpha1: number; // ARCH coefficient (α) - impact of recent shocks
  beta1: number;  // GARCH coefficient (β) - persistence of volatility
  mu: number;     // Mean return
  
  // Constraints for stability
  isStationary: boolean; // alpha1 + beta1 < 1
  unconditionalVolatility: number; // sqrt(α₀ / (1 - α₁ - β₁))
}

export interface GARCHCalibrationData {
  historicalReturns: number[];
  estimationMethod: 'moment-matching' | 'maximum-likelihood';
  calibrationDate?: string;
  dataSource?: string;
}

export interface GARCHPathConfig {
  periods: number;
  initialVariance?: number;
  includeJumps?: boolean;
  jumpProbability?: number;
  jumpMagnitude?: number;
}

/**
 * GARCH(1,1) Path Generator
 */
export class GARCHPathGenerator {
  private params: GARCHParameters;
  
  constructor(params: GARCHParameters) {
    // Validate stationarity condition
    if (params.alpha1 + params.beta1 >= 1) {
      throw new Error(
        `GARCH parameters violate stationarity: α₁ + β₁ = ${params.alpha1 + params.beta1} >= 1. ` +
        'Non-stationary processes can lead to explosive volatility.'
      );
    }
    
    // Validate parameter ranges
    if (params.alpha0 <= 0 || params.alpha1 < 0 || params.beta1 < 0) {
      throw new Error('GARCH parameters must be non-negative (α₀ > 0, α₁ ≥ 0, β₁ ≥ 0)');
    }
    
    this.params = params;
  }
  
  /**
   * Generate a single GARCH(1,1) path
   */
  generatePath(config: GARCHPathConfig): {
    returns: number[];
    volatilities: number[];
    shocks: number[];
  } {
    const { periods, initialVariance, includeJumps = false, jumpProbability = 0.01, jumpMagnitude = 0.05 } = config;
    
    const returns: number[] = [];
    const volatilities: number[] = [];
    const shocks: number[] = [];
    
    // Initialize variance
    let variance = initialVariance || this.params.unconditionalVolatility ** 2;
    
    for (let t = 0; t < periods; t++) {
      // Generate standardized normal shock
      const z = this.boxMuller()[0];
      
      // Add jump component if enabled
      let jumpComponent = 0;
      if (includeJumps && Math.random() < jumpProbability) {
        jumpComponent = (Math.random() < 0.5 ? -1 : 1) * jumpMagnitude;
      }
      
      // Calculate volatility and return
      const sigma = Math.sqrt(variance);
      const epsilon = sigma * z;
      const ret = this.params.mu + epsilon + jumpComponent;
      
      returns.push(ret);
      volatilities.push(sigma);
      shocks.push(epsilon);
      
      // Update variance for next period: σ²_{t+1} = α₀ + α₁ε²_t + β₁σ²_t
      variance = this.params.alpha0 + 
                 this.params.alpha1 * Math.pow(epsilon, 2) + 
                 this.params.beta1 * variance;
      
      // Ensure variance stays positive (numerical stability)
      variance = Math.max(variance, 1e-8);
    }
    
    return { returns, volatilities, shocks };
  }
  
  /**
   * Generate multiple GARCH paths for Monte Carlo simulation
   */
  generateMultiplePaths(
    numPaths: number,
    config: GARCHPathConfig
  ): Array<{ returns: number[]; volatilities: number[]; shocks: number[] }> {
    const paths: Array<{ returns: number[]; volatilities: number[]; shocks: number[] }> = [];
    
    for (let i = 0; i < numPaths; i++) {
      paths.push(this.generatePath(config));
    }
    
    return paths;
  }
  
  /**
   * Box-Muller transformation for generating normal random variables
   */
  private boxMuller(): [number, number] {
    let u1 = 0, u2 = 0;
    
    // Ensure we don't get log(0)
    while (u1 === 0) u1 = Math.random();
    while (u2 === 0) u2 = Math.random();
    
    const z1 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const z2 = Math.sqrt(-2 * Math.log(u1)) * Math.sin(2 * Math.PI * u2);
    
    return [z1, z2];
  }
  
  /**
   * Get theoretical moments of the GARCH process
   */
  getTheoreticalMoments(): {
    mean: number;
    variance: number;
    unconditionalVolatility: number;
    excessKurtosis: number;
  } {
    const unconditionalVariance = this.params.alpha0 / (1 - this.params.alpha1 - this.params.beta1);
    
    // Excess kurtosis for GARCH(1,1): κ = 6α₁² / (1 - β₁² - 2α₁β₁ - 3α₁²)
    const denominator = 1 - this.params.beta1 ** 2 - 2 * this.params.alpha1 * this.params.beta1 - 3 * this.params.alpha1 ** 2;
    const excessKurtosis = denominator > 0 
      ? (6 * this.params.alpha1 ** 2) / denominator 
      : Infinity; // Non-finite kurtosis
    
    return {
      mean: this.params.mu,
      variance: unconditionalVariance,
      unconditionalVolatility: Math.sqrt(unconditionalVariance),
      excessKurtosis
    };
  }
  
  /**
   * Calibrate GARCH parameters from historical data
   */
  static calibrateFromData(data: GARCHCalibrationData): GARCHParameters {
    const { historicalReturns, estimationMethod } = data;
    
    if (historicalReturns.length < 100) {
      throw new Error('Need at least 100 observations for reliable GARCH calibration');
    }
    
    if (estimationMethod === 'moment-matching') {
      return this.momentMatchingEstimation(historicalReturns);
    } else {
      // For maximum likelihood, we'd use numerical optimization (e.g., Nelder-Mead)
      // This is a placeholder - production should use a proper optimization library
      console.warn('Maximum likelihood estimation not fully implemented. Using moment matching.');
      return this.momentMatchingEstimation(historicalReturns);
    }
  }
  
  /**
   * Moment matching estimation (simplified approach)
   */
  private static momentMatchingEstimation(returns: number[]): GARCHParameters {
    // Calculate sample moments
    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const demeaned = returns.map(r => r - mean);
    const variance = demeaned.reduce((sum, r) => sum + r ** 2, 0) / returns.length;
    
    // Calculate autocorrelation of squared returns
    const squaredReturns = demeaned.map(r => r ** 2);
    const rho1 = this.autocorrelation(squaredReturns, 1);
    
    // Estimate GARCH parameters using method of moments
    // These are typical values from empirical studies of equity markets
    const alpha1 = 0.05; // ARCH effect (5% weight on recent shock)
    const beta1 = Math.min(0.90, rho1); // GARCH effect (high persistence)
    const alpha0 = variance * (1 - alpha1 - beta1); // Long-run variance
    
    const persistence = alpha1 + beta1;
    const unconditionalVol = Math.sqrt(variance);
    
    return {
      alpha0,
      alpha1,
      beta1,
      mu: mean,
      isStationary: persistence < 1,
      unconditionalVolatility: unconditionalVol
    };
  }
  
  /**
   * Calculate autocorrelation at lag k
   */
  private static autocorrelation(series: number[], lag: number): number {
    const n = series.length;
    const mean = series.reduce((sum, val) => sum + val, 0) / n;
    
    let numerator = 0;
    let denominator = 0;
    
    for (let i = 0; i < n; i++) {
      denominator += Math.pow(series[i] - mean, 2);
      
      if (i >= lag) {
        numerator += (series[i] - mean) * (series[i - lag] - mean);
      }
    }
    
    return numerator / denominator;
  }
  
  /**
   * Default GARCH parameters for Australian equities
   * Based on ASX 200 historical analysis
   */
  static getDefaultAustralianEquityParams(): GARCHParameters {
    return {
      alpha0: 0.00002,  // Long-term variance
      alpha1: 0.08,      // ARCH coefficient
      beta1: 0.90,       // GARCH coefficient (high persistence)
      mu: 0.0008,        // Daily mean return (~20% annualized)
      isStationary: true,
      unconditionalVolatility: 0.014 // ~14% daily vol, ~22% annualized
    };
  }
  
  /**
   * Default GARCH parameters for global equities
   */
  static getDefaultGlobalEquityParams(): GARCHParameters {
    return {
      alpha0: 0.000015,
      alpha1: 0.07,
      beta1: 0.91,
      mu: 0.0007,        // ~18% annualized
      isStationary: true,
      unconditionalVolatility: 0.012 // ~19% annualized
    };
  }
  
  /**
   * Default GARCH parameters for fixed income
   */
  static getDefaultFixedIncomeParams(): GARCHParameters {
    return {
      alpha0: 0.000005,
      alpha1: 0.05,
      beta1: 0.88,
      mu: 0.0003,        // ~7% annualized
      isStationary: true,
      unconditionalVolatility: 0.006 // ~9.5% annualized
    };
  }
}
